import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { 
    Building2, Users, Truck, UserCheck, TrendingUp, 
    DollarSign, Calendar, ChevronRight, Loader2,
    Shield, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

export default function SuperAdminDashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await api.get('/super-admin/stats');
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch stats:', error);
            toast.error('Failed to load platform statistics');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
            </div>
        );
    }

    const planColors = {
        personal: 'bg-slate-500',
        basic: 'bg-[#1B7EB5]',
        professional: 'bg-[#8CC774]',
        enterprise: 'bg-purple-600'
    };

    return (
        <div className="space-y-6" data-testid="super-admin-dashboard">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <Shield className="w-6 h-6 text-purple-600" />
                        <span className="text-sm text-purple-600 font-medium">Super Admin</span>
                    </div>
                    <h1 className="text-2xl font-bold text-slate-800">Platform Overview</h1>
                    <p className="text-slate-500">Welcome back, {user?.name}</p>
                </div>
                <div className="flex gap-3">
                    <Button 
                        onClick={() => navigate('/app/super-admin/companies')}
                        className="bg-[#1B7EB5] hover:bg-[#1B7EB5]/90 text-white"
                    >
                        <Building2 className="w-4 h-4 mr-2" />
                        Manage Companies
                    </Button>
                    <Button 
                        onClick={() => navigate('/app/super-admin/users')}
                        variant="outline"
                        className="border-slate-300"
                    >
                        <Users className="w-4 h-4 mr-2" />
                        All Users
                    </Button>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="fleet-card p-4">
                    <div className="flex items-center justify-between mb-2">
                        <Building2 className="w-5 h-5 text-[#1B7EB5]" />
                        <span className="text-xs text-[#8CC774]">+{stats?.recent_signups?.length || 0} recent</span>
                    </div>
                    <p className="text-3xl font-bold text-slate-800">{stats?.total_companies || 0}</p>
                    <p className="text-sm text-slate-500">Total Companies</p>
                </div>

                <div className="fleet-card p-4">
                    <div className="flex items-center justify-between mb-2">
                        <Users className="w-5 h-5 text-[#58A6B4]" />
                    </div>
                    <p className="text-3xl font-bold text-slate-800">{stats?.total_users || 0}</p>
                    <p className="text-sm text-slate-500">Total Users</p>
                </div>

                <div className="fleet-card p-4">
                    <div className="flex items-center justify-between mb-2">
                        <Truck className="w-5 h-5 text-[#8CC774]" />
                    </div>
                    <p className="text-3xl font-bold text-slate-800">{stats?.total_vehicles || 0}</p>
                    <p className="text-sm text-slate-500">Total Vehicles</p>
                </div>

                <div className="fleet-card p-4">
                    <div className="flex items-center justify-between mb-2">
                        <UserCheck className="w-5 h-5 text-purple-600" />
                    </div>
                    <p className="text-3xl font-bold text-slate-800">{stats?.total_drivers || 0}</p>
                    <p className="text-sm text-slate-500">Total Drivers</p>
                </div>
            </div>

            {/* Companies by Plan & Recent Signups */}
            <div className="grid md:grid-cols-2 gap-6">
                {/* Companies by Plan */}
                <div className="fleet-card p-4">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-[#1B7EB5]" />
                        Companies by Plan
                    </h3>
                    <div className="space-y-3">
                        {Object.entries(stats?.companies_by_plan || {}).map(([plan, count]) => (
                            <div key={plan} className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className={`w-3 h-3 rounded-full ${planColors[plan] || 'bg-gray-400'}`} />
                                    <span className="text-slate-700 capitalize">{plan}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                                        <div 
                                            className={`h-full ${planColors[plan] || 'bg-gray-400'}`}
                                            style={{ width: `${(count / (stats?.total_companies || 1)) * 100}%` }}
                                        />
                                    </div>
                                    <span className="text-sm font-medium text-slate-800 w-8">{count}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Recent Signups */}
                <div className="fleet-card p-4">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-[#8CC774]" />
                            Recent Signups
                        </h3>
                        <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate('/app/super-admin/companies')}
                            className="text-[#1B7EB5]"
                        >
                            View All <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                    <div className="space-y-3">
                        {stats?.recent_signups?.slice(0, 5).map((company) => (
                            <div 
                                key={company.id} 
                                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                                onClick={() => navigate(`/app/super-admin/companies/${company.id}`)}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-[#1B7EB5]/10 rounded-lg flex items-center justify-center">
                                        <Building2 className="w-5 h-5 text-[#1B7EB5]" />
                                    </div>
                                    <div>
                                        <p className="text-slate-800 font-medium text-sm">{company.name}</p>
                                        <p className="text-xs text-slate-500 capitalize">{company.plan} Plan</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-slate-500">
                                        {new Date(company.created_at).toLocaleDateString()}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="fleet-card p-4">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Quick Actions</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Button 
                        variant="outline" 
                        className="h-auto py-4 flex flex-col items-center gap-2 border-slate-200 hover:border-[#1B7EB5] hover:bg-[#1B7EB5]/5"
                        onClick={() => navigate('/app/super-admin/companies')}
                    >
                        <Building2 className="w-6 h-6 text-[#1B7EB5]" />
                        <span className="text-sm text-slate-700">View Companies</span>
                    </Button>
                    <Button 
                        variant="outline" 
                        className="h-auto py-4 flex flex-col items-center gap-2 border-slate-200 hover:border-[#58A6B4] hover:bg-[#58A6B4]/5"
                        onClick={() => navigate('/app/super-admin/users')}
                    >
                        <Users className="w-6 h-6 text-[#58A6B4]" />
                        <span className="text-sm text-slate-700">Manage Users</span>
                    </Button>
                    <Button 
                        variant="outline" 
                        className="h-auto py-4 flex flex-col items-center gap-2 border-slate-200 hover:border-[#8CC774] hover:bg-[#8CC774]/5"
                        onClick={() => navigate('/app/analytics')}
                    >
                        <TrendingUp className="w-6 h-6 text-[#8CC774]" />
                        <span className="text-sm text-slate-700">Platform Analytics</span>
                    </Button>
                    <Button 
                        variant="outline" 
                        className="h-auto py-4 flex flex-col items-center gap-2 border-slate-200 hover:border-purple-500 hover:bg-purple-500/5"
                        onClick={() => toast.info('Billing integration coming soon')}
                    >
                        <DollarSign className="w-6 h-6 text-purple-500" />
                        <span className="text-sm text-slate-700">Billing Overview</span>
                    </Button>
                </div>
            </div>
        </div>
    );
}
